import React, { useState, useEffect, useCallback } from 'react';
import { offlineHelpers } from '../../services/db';
import './OfflineIndicator.css';

interface OfflineIndicatorProps {
  isOnline: boolean;
  pendingCount: number;
  onRetrySync?: () => Promise<void>;
}

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

/**
 * Enhanced offline indicator with sync status and retry functionality
 */
const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  isOnline,
  pendingCount,
  onRetrySync,
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0 && syncStatus === 'idle') {
      handleSync();
    }
  }, [isOnline, pendingCount]);

  const handleSync = useCallback(async () => {
    if (!isOnline || syncStatus === 'syncing') return;

    setSyncStatus('syncing');
    
    try {
      if (onRetrySync) {
        await onRetrySync();
      } else {
        await offlineHelpers.syncQueue();
      }
      
      setSyncStatus('success');
      setLastSyncTime(new Date());
      
      // Reset status after a delay
      setTimeout(() => {
        setSyncStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncStatus('error');
      
      // Reset status after a delay
      setTimeout(() => {
        setSyncStatus('idle');
      }, 5000);
    }
  }, [isOnline, syncStatus, onRetrySync]);

  // Don't show if online with no pending items
  if (isOnline && pendingCount === 0 && syncStatus === 'idle') {
    return null;
  }

  const getStatusIcon = () => {
    if (!isOnline) {
      return (
        <svg className="status-icon offline" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.64 7c-.45-.34-4.93-4-11.64-4-1.5 0-2.89.19-4.15.48L18.18 13.8 23.64 7zm-6.6 8.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7L12 21.5l3.07-4.04 5.35 5.36 1.27-1.27-4.65-4.65-1.27-1.27-3.73-3.43z"/>
        </svg>
      );
    }
    
    switch (syncStatus) {
      case 'syncing':
        return (
          <svg className="status-icon syncing" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
          </svg>
        );
      case 'success':
        return (
          <svg className="status-icon success" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        );
      case 'error':
        return (
          <svg className="status-icon error" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
        );
      default:
        return (
          <svg className="status-icon pending" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        );
    }
  };

  const getStatusText = () => {
    if (!isOnline) {
      return 'You are offline';
    }
    
    switch (syncStatus) {
      case 'syncing':
        return 'Syncing...';
      case 'success':
        return 'Synced successfully!';
      case 'error':
        return 'Sync failed';
      default:
        if (pendingCount > 0) {
          return `${pendingCount} pending ${pendingCount === 1 ? 'item' : 'items'}`;
        }
        return 'All synced';
    }
  };

  const formatLastSync = () => {
    if (!lastSyncTime) return null;
    
    const now = new Date();
    const diff = now.getTime() - lastSyncTime.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    
    return lastSyncTime.toLocaleTimeString();
  };

  return (
    <div 
      className={`offline-indicator ${!isOnline ? 'offline' : ''} ${syncStatus}`}
      onClick={() => setIsExpanded(!isExpanded)}
      role="status"
      aria-live="polite"
    >
      <div className="indicator-main">
        {getStatusIcon()}
        <span className="indicator-text">{getStatusText()}</span>
        
        {isOnline && pendingCount > 0 && syncStatus !== 'syncing' && (
          <button 
            className="sync-button"
            onClick={(e) => {
              e.stopPropagation();
              handleSync();
            }}
            aria-label="Retry sync"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
            </svg>
            Retry
          </button>
        )}
      </div>
      
      {isExpanded && (
        <div className="indicator-details">
          {!isOnline && (
            <p>Changes will sync automatically when connection is restored.</p>
          )}
          {lastSyncTime && (
            <p className="last-sync">Last synced: {formatLastSync()}</p>
          )}
          {pendingCount > 0 && (
            <p className="pending-info">
              {pendingCount} {pendingCount === 1 ? 'change' : 'changes'} waiting to sync
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;

